'use client';

import { useState, useMemo } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Label } from '@/components/ui/label';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { Input } from '@/components/ui/input';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '@/components/ui/accordion';
import { Shuffle, Globe, Check, Trash2 } from 'lucide-react';

interface SplitConfigDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  route: {
    id: string;
    matcher: string;
    hostname: string;
    basePath: string;
  };
  availableInstances: any[];
  existingConfig?: {
    type: 'weighted' | 'geo';
    instances: {
      instanceId: string;
      weight?: number;
      regions?: string[];
    }[];
    stickyDuration: number;
  };
  onSave: (splitConfig: any) => void;
}

export function SplitConfigDialog({
  open,
  onOpenChange,
  route,
  availableInstances,
  existingConfig,
  onSave,
}: SplitConfigDialogProps) {
  const [splitType, setSplitType] = useState<'weighted' | 'geo'>(existingConfig?.type || 'weighted');
  const [splitInstances, setSplitInstances] = useState<any[]>(existingConfig?.instances || []);
  const [stickyDuration, setStickyDuration] = useState(existingConfig?.stickyDuration || 604800);

  const groupedInstances = useMemo(() => {
    const groups = new Map<string, Map<string, Map<string, any[]>>>();
    // Filter out preview/forked instances (only show production instances)
    const productionInstances = availableInstances.filter((instance) => !instance.isPreview);
    productionInstances.forEach((instance) => {
      if (!groups.has(instance.pluginId)) groups.set(instance.pluginId, new Map());
      const pluginGroup = groups.get(instance.pluginId)!;
      if (!pluginGroup.has(instance.version)) pluginGroup.set(instance.version, new Map());
      const versionGroup = pluginGroup.get(instance.version)!;
      // Use direct configName property (available in lightweight mode) or fallback to config.configName
      const configName = instance.configName || instance.config?.configName || 'default';
      if (!versionGroup.has(configName)) versionGroup.set(configName, []);
      versionGroup.get(configName)!.push(instance);
    });
    return groups;
  }, [availableInstances]);

  const handleAddInstance = (instanceId: string) => {
    const instance = availableInstances.find((i) => i.id === instanceId);
    if (!instance) return;
    if (splitType === 'weighted') {
      setSplitInstances([...splitInstances, { instanceId, weight: 0 }]);
    } else {
      setSplitInstances([...splitInstances, { instanceId, regions: [] }]);
    }
  };

  const handleRemoveInstance = (index: number) => {
    setSplitInstances(splitInstances.filter((_, i) => i !== index));
  };

  const handleUpdateWeight = (index: number, weight: number) => {
    const updated = [...splitInstances];
    updated[index] = { ...updated[index], weight };
    setSplitInstances(updated);
  };

  const handleUpdateRegions = (index: number, regions: string) => {
    const updated = [...splitInstances];
    updated[index] = {
      ...updated[index],
      regions: regions
        .split(',')
        .map((r) => r.trim())
        .filter(Boolean),
    };
    setSplitInstances(updated);
  };

  const handleSave = () => {
    if (splitInstances.length < 2) {
      alert('At least 2 instances are required for split testing');
      return;
    }
    if (splitType === 'weighted') {
      const totalWeight = splitInstances.reduce((sum, inst) => sum + (inst.weight || 0), 0);
      if (totalWeight !== 100) {
        alert('Total weight must equal 100%');
        return;
      }
    } else {
      const hasInvalidRegions = splitInstances.some((inst) => !inst.regions || inst.regions.length === 0);
      if (hasInvalidRegions) {
        alert('All instances must have at least one region defined');
        return;
      }
    }
    onSave({ type: splitType, instances: splitInstances, stickyDuration });
    onOpenChange(false);
  };

  const isInstanceSelected = (instanceId: string) =>
    splitInstances.some((inst) => inst.instanceId === instanceId);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-h-[80vh] max-w-3xl overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Configure Split Testing</DialogTitle>
          <div className="text-muted-foreground text-sm">
            Route: <code className="bg-muted rounded px-1 py-0.5 text-xs">{route.matcher}</code> on{' '}
            <code className="bg-muted rounded px-1 py-0.5 text-xs">
              {route.hostname}
              {route.basePath}
            </code>
          </div>
        </DialogHeader>
        <div className="space-y-4">
          <div className="space-y-2">
            <Label>Split Type</Label>
            <RadioGroup value={splitType} onValueChange={(value: any) => setSplitType(value)}>
              <div className="flex items-center space-x-2">
                <RadioGroupItem value="weighted" id="weighted" />
                <Label htmlFor="weighted" className="flex cursor-pointer items-center gap-1 font-normal">
                  <Shuffle className="h-3.5 w-3.5" />
                  Weighted (A/B Testing)
                </Label>
              </div>
              <div className="flex items-center space-x-2">
                <RadioGroupItem value="geo" id="geo" />
                <Label htmlFor="geo" className="flex cursor-pointer items-center gap-1 font-normal">
                  <Globe className="h-3.5 w-3.5" />
                  Geographic Routing
                </Label>
              </div>
            </RadioGroup>
          </div>
          <div className="space-y-2">
            <Label>Sticky Duration (seconds)</Label>
            <Input
              type="number"
              value={stickyDuration}
              onChange={(e) => setStickyDuration(parseInt(e.target.value))}
              placeholder="604800"
            />
            <p className="text-muted-foreground text-xs">
              How long to keep users on the same instance (default: 7 days = 604800 seconds)
            </p>
          </div>
          {splitInstances.length > 0 && (
            <div className="space-y-2">
              <Label>Selected Instances ({splitInstances.length})</Label>
              <div className="divide-y rounded-md border">
                {splitInstances.map((splitInst, index) => {
                  const instance = availableInstances.find((i) => i.id === splitInst.instanceId);
                  if (!instance) return null;
                  return (
                    <div key={index} className="flex items-start gap-3 p-3">
                      <div className="flex-1">
                        <div className="mb-2 flex items-center gap-2">
                          <Badge variant="outline" className="text-xs">
                            {instance.configName || instance.config?.configName || 'default'}
                          </Badge>
                          <span className="text-muted-foreground text-xs">v{instance.version}</span>
                          {instance.isPreview && (
                            <Badge variant="secondary" className="text-xs">
                              🧪 Preview
                            </Badge>
                          )}
                        </div>
                        {splitType === 'weighted' ? (
                          <div className="flex items-center gap-2">
                            <Input
                              type="number"
                              min="0"
                              max="100"
                              value={splitInst.weight || 0}
                              onChange={(e) => handleUpdateWeight(index, parseInt(e.target.value))}
                              className="w-20"
                            />
                            <span className="text-sm">%</span>
                          </div>
                        ) : (
                          <Input
                            placeholder="US, CA, GB (comma-separated)"
                            value={splitInst.regions?.join(', ') || ''}
                            onChange={(e) => handleUpdateRegions(index, e.target.value)}
                          />
                        )}
                      </div>
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={() => handleRemoveInstance(index)}
                        className="text-destructive"
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </div>
                  );
                })}
              </div>
            </div>
          )}
          <div className="space-y-2">
            <Label>Add Instances</Label>
            <Accordion type="single" collapsible className="rounded-md border">
              {Array.from(groupedInstances.entries()).map(([pluginId, versionMap]) => {
                const pluginName =
                  availableInstances.find((i) => i.pluginId === pluginId)?.pluginName || pluginId;
                return (
                  <AccordionItem key={pluginId} value={pluginId}>
                    <AccordionTrigger className="px-3 py-2">
                      <div className="flex items-center gap-2">
                        <span className="font-medium">{pluginName}</span>
                        <Badge variant="secondary" className="text-xs">
                          {Array.from(versionMap.values()).reduce(
                            (sum, configs) =>
                              sum + Array.from(configs.values()).reduce((s, insts) => s + insts.length, 0),
                            0,
                          )}{' '}
                          instances
                        </Badge>
                      </div>
                    </AccordionTrigger>
                    <AccordionContent className="px-3 pb-2">
                      {Array.from(versionMap.entries()).map(([version, configMap]) => (
                        <div key={version} className="mb-2">
                          <div className="mb-1 text-sm font-medium">v{version}</div>
                          <div className="space-y-1 pl-2">
                            {Array.from(configMap.entries()).map(([configName, instances]) => (
                              <div key={configName}>
                                {instances.map((instance) => {
                                  const isSelected = isInstanceSelected(instance.id);
                                  return (
                                    <button
                                      key={instance.id}
                                      onClick={() => !isSelected && handleAddInstance(instance.id)}
                                      disabled={isSelected}
                                      className={`flex w-full items-center gap-2 rounded px-2 py-1 text-left text-xs ${isSelected ? 'bg-muted cursor-not-allowed opacity-50' : 'hover:bg-muted cursor-pointer'}`}
                                    >
                                      {isSelected && <Check className="h-3 w-3" />}
                                      <Badge variant="outline" className="text-xs">
                                        {configName}
                                      </Badge>
                                    </button>
                                  );
                                })}
                              </div>
                            ))}
                          </div>
                        </div>
                      ))}
                    </AccordionContent>
                  </AccordionItem>
                );
              })}
            </Accordion>
          </div>
          <div className="flex justify-end gap-2 border-t pt-4">
            <Button variant="outline" onClick={() => onOpenChange(false)}>
              Cancel
            </Button>
            <Button onClick={handleSave}>Save Split Configuration</Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
